import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  Seo,
  blogPostingJsonLd,
  breadcrumbJsonLd,
} from "@/components/Seo";
import { formatDate } from "@/lib/date";
import { getPost } from "@/content/blog/posts";

const BlogPost = () => {
  const { slug } = useParams();
  const post = getPost(slug);

  if (!post) {
    // Unknown slug — behave like a not-found page (no index, clear recovery link)
    // rather than rendering a broken shell.
    return (
      <div className="min-h-screen bg-background">
        <Seo title="Post not found" path={`/blog/${slug ?? ""}`} noindex />
        <Navbar />
        <main id="main-content" tabIndex={-1} className="section-padding pt-28 md:pt-[9rem]">
          <div className="mx-auto max-w-2xl text-center">
            <h1 className="mb-4 font-display text-3xl font-bold text-foreground">
              Post not found
            </h1>
            <p className="mb-6 text-muted-foreground">
              We couldn&rsquo;t find that article. It may have been moved or removed.
            </p>
            <Link
              to="/blog"
              className="text-primary underline hover:text-primary/90 focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
            >
              Back to all posts
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const path = `/blog/${post.slug}`;

  return (
    <div className="min-h-screen bg-background">
      <Seo
        title={post.metaTitle ?? post.title}
        description={post.metaDescription ?? post.excerpt}
        path={path}
        image={post.photo}
        type="article"
        jsonLd={[
          blogPostingJsonLd({
            title: post.title,
            excerpt: post.excerpt,
            path,
            image: post.photo,
            datePublished: post.date,
            dateModified: post.updated,
            author: post.author,
            keywords: post.tag,
            wordCount: post.body.trim().split(/\s+/).length,
          }),
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Blog", path: "/blog" },
            { name: post.title, path },
          ]),
        ]}
      />
      <Navbar />
      <main id="main-content" tabIndex={-1}>
        <article className="section-padding pt-28 md:pt-[9rem]">
          <div className="mx-auto max-w-3xl">
            {/* Breadcrumb */}
            <nav aria-label="Breadcrumb" className="mb-8">
              <ol className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                <li>
                  <Link
                    to="/"
                    className="hover:text-foreground focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-focus-ring rounded-sm"
                  >
                    Home
                  </Link>
                </li>
                <li aria-hidden="true">/</li>
                <li>
                  <Link
                    to="/blog"
                    className="hover:text-foreground focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-focus-ring rounded-sm"
                  >
                    Blog
                  </Link>
                </li>
                <li aria-hidden="true">/</li>
                <li>
                  <span aria-current="page" className="text-foreground">
                    {post.title}
                  </span>
                </li>
              </ol>
            </nav>

            {/* Fixed template: tag/date · title · subtitle · photo · body */}
            <header className="mb-8">
              <div className="mb-4 flex items-center gap-3 text-sm text-muted-foreground">
                {post.tag && <span className="badge-pill">{post.tag}</span>}
                <time dateTime={post.date}>{formatDate(post.date)}</time>
              </div>
              <h1 className="font-display text-3xl font-bold leading-tight text-foreground md:text-4xl">
                {post.title}
              </h1>
              <p className="mt-4 text-lg text-muted-foreground">{post.subtitle}</p>
            </header>

            {post.photo && (
              <img
                src={post.photo}
                alt={post.alt ?? ""}
                width={1200}
                height={630}
                className="mb-10 aspect-[16/9] w-full rounded-2xl border object-cover"
              />
            )}

            <div className="prose prose-neutral dark:prose-invert max-w-none prose-lg prose-headings:font-display prose-a:text-primary prose-img:rounded-xl">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.body}</ReactMarkdown>
            </div>

            <hr className="my-12 border-border" />
            <Link
              to="/blog"
              className="inline-flex min-h-[44px] items-center gap-2 text-sm font-medium text-primary hover:text-primary/90 focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-focus-ring rounded-sm"
            >
              <span aria-hidden="true">&larr;</span> Back to all posts
            </Link>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPost;
